import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { EMPTY_SHIP_FROM_ROW } from "../../constants/shipFromTransitTime"
import { useSaveShipFromTransitTime } from "../../hooks/useSaveShipFromTransitTime"
import { type ShipFromTransitTimeFormValues, shipFromTransitTimeSchema } from "../../schemas/shipFromTransitTime"

import { ShipFromTransitTimeForm } from "./ShipFromTransitTimeForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { ShipFromPreferenceItemInput } from "@/graphql/codegen/graphql"

interface EditShipFromTransitTimeDialogProps {
  onClose: (shouldRefetch?: boolean) => void
  locationId: string
  initialData?: ShipFromPreferenceItemInput[]
}

export const EditShipFromTransitTimeDialog: FC<EditShipFromTransitTimeDialogProps> = ({
  onClose,
  locationId,
  initialData = [],
}) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const { saveShipFromTransitTime, isSavingTransitTime } = useSaveShipFromTransitTime({
    successMessage: "Ship From / Transit Time preferences saved successfully",
    onSuccess: () => onClose(true),
  })

  const isEditMode = initialData.length > 0
  const dialogTitle = isEditMode ? "Edit Ship From / Transit Time" : "Add Ship From / Transit Time"

  const defaultValues: ShipFromTransitTimeFormValues = {
    rows: initialData.length > 0 ? initialData : [EMPTY_SHIP_FROM_ROW],
  }

  const methods = useForm<ShipFromTransitTimeFormValues>({
    defaultValues,
    resolver: zodResolver(shipFromTransitTimeSchema),
    mode: "onChange",
  })

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting, isValid, errors },
    control,
    trigger,
  } = methods

  const handleWarehouseChange = () => {
    void trigger("rows")
  }

  const handleClose = () => {
    if (isDirty) {
      setShowCancelDialog(true)
    } else {
      onClose()
    }
  }

  const handleCancelDialogClose = () => {
    setShowCancelDialog(false)
  }

  const handleConfirmClose = () => {
    setShowCancelDialog(false)
    onClose()
  }

  const onSubmit = async (data: ShipFromTransitTimeFormValues) => {
    await saveShipFromTransitTime({
      locationId,
      rows: data.rows,
    })
  }

  return (
    <>
      <Dialog open onClose={handleClose} fullWidth maxWidth='md' data-testid='ship-from-dialog'>
        <DialogHeader title={dialogTitle} onClose={handleClose} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent className='px-10 pb-1 pt-8'>
            {errors.rows?.message && (
              <Box className='mb-4 rounded-md border border-red-300 bg-red-50 p-3'>
                <Typography variant='body2' className='text-red-800'>
                  {errors.rows.message}
                </Typography>
              </Box>
            )}
            <FormProvider {...methods}>
              <ShipFromTransitTimeForm control={control} onWarehouseChange={handleWarehouseChange} />
            </FormProvider>
          </DialogContent>

          <DialogActions className='justify-between p-8'>
            <Button
              appearance='outlined'
              onClick={handleClose}
              disabled={isSubmitting}
              data-testid='ship-from-cancel-button'
            >
              Cancel
            </Button>
            <Button
              type='submit'
              disabled={!isValid || isSubmitting || isSavingTransitTime}
              data-testid='ship-from-save-button'
            >
              {isSavingTransitTime ? "Saving..." : "Save Changes"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {showCancelDialog && <CancelDialog onClose={handleCancelDialogClose} onConfirm={handleConfirmClose} />}
    </>
  )
}
